import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, imageUrl } from '../api/client.js';
import ShareButton from '../components/ShareButton.jsx';
import AudioPlayer from '../components/AudioPlayer.jsx';
import { BookIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from '../components/icons.jsx';
import { logCostBreakdown } from '../utils/costLog.js';

export default function ReaderPage() {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  // Si el usuario inicio la reproduccion en alguna pagina (y no la ha pausado
  // manualmente), encadenamos el audio de la siguiente pagina al navegar.
  const [autoplayMode, setAutoplayMode] = useState(false);

  useEffect(() => {
    api.stories
      .bySlug(slug)
      .then((s) => {
        setStory(s);
        if (s?.cost) logCostBreakdown(s.title, s.cost);
      })
      .catch((e) => setError(e.message));
  }, [slug]);

  const totalPages = (story?.scenes?.length ?? 0) + 1; // +1 portada

  const goPrev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
  const goNext = useCallback(() => setPage((p) => Math.min(totalPages - 1, p + 1)), [totalPages]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <p className="text-red-600">{error}</p>
        <Link to="/" className="mt-4 inline-block text-brand-600 underline">Volver al inicio</Link>
      </div>
    );
  }
  if (!story) {
    return <p className="mx-auto max-w-2xl px-4 py-10 text-gray-500">Cargando...</p>;
  }

  const onCover = page === 0;
  const sceneIndex = page - 1;
  const scene = story.scenes?.[sceneIndex];
  const isReady = story.status === 'ready';
  const isLastScene = !onCover && sceneIndex === (story.scenes?.length ?? 0) - 1;

  // Layout que cabe en el viewport: el header del Layout es ~56px,
  // dejamos el resto para tarjeta + controles + acciones, sin scroll.
  return (
    <div
      className="mx-auto flex w-full max-w-4xl flex-col gap-3 px-3 pb-3 pt-3"
      style={{ height: 'calc(100dvh - 64px)' }}
    >
      {/* Tarjeta del cuento */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-brand-100 bg-white shadow-lg">
        {/* Imagen: flex-1 y object-contain → ocupa el alto restante manteniendo ratio */}
        <div className="flex min-h-0 flex-1 items-center justify-center bg-brand-50">
          {onCover && story.character_sheet_image_id ? (
            <img
              src={imageUrl(story.character_sheet_image_id)}
              alt={story.title}
              className="h-full w-full object-contain"
            />
          ) : !onCover && scene?.image_id ? (
            <img
              src={imageUrl(scene.image_id)}
              alt={`Escena ${sceneIndex + 1}`}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="text-brand-300"><BookIcon size="64" /></div>
          )}
        </div>

        {/* Texto: altura limitada, el propio bloque scrollea si fuera muy largo */}
        <div className="max-h-[35%] shrink-0 overflow-auto border-t border-brand-50 px-4 py-3">
          {onCover ? (
            <div className="text-center">
              <h1 className="text-xl font-bold text-brand-700 sm:text-2xl">{story.title}</h1>
              {story.synopsis && (
                <p className="mt-1 text-sm text-gray-600 sm:text-base">{story.synopsis}</p>
              )}
              {!isReady && (
                <p className="mt-2 text-xs text-amber-700">
                  Este cuento está en proceso ({story.status}).
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="mb-1 text-center text-[10px] font-semibold uppercase tracking-wide text-brand-500">
                Escena {sceneIndex + 1} de {story.scenes.length}
              </div>
              <p className="whitespace-pre-line text-center text-base leading-relaxed text-gray-800 sm:text-lg">
                {scene?.text}
              </p>
              {isLastScene && (
                <div className="mt-3 flex items-center justify-center gap-3" aria-label="Fin del cuento">
                  <span className="h-px w-10 bg-brand-300" />
                  <span className="rounded-full border-2 border-brand-400 bg-brand-50 px-5 py-1 text-xl font-extrabold uppercase tracking-[0.3em] text-brand-700 shadow-sm sm:text-2xl">
                    Fin
                  </span>
                  <span className="h-px w-10 bg-brand-300" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Navegación + acciones en una sola fila */}
      <div className="flex shrink-0 items-center justify-between gap-2">
        <button
          onClick={goPrev}
          disabled={page === 0}
          aria-label="Anterior"
          className="inline-flex items-center gap-1 rounded-full border border-brand-200 px-3 py-1.5 text-sm font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-40 sm:px-5 sm:py-2"
        >
          <ChevronLeftIcon size="1em" />
          <span className="hidden sm:inline">Anterior</span>
        </button>

        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{page + 1} / {totalPages}</span>
          {isReady && (
            <AudioPlayer
              story={story}
              part={onCover ? 'cover' : { sceneIndex }}
              autoplay={autoplayMode}
              onUserPlay={() => setAutoplayMode(true)}
              onUserPause={() => setAutoplayMode(false)}
              onCostUpdated={(breakdown) => {
                setStory((s) => {
                  if (!s) return s;
                  // Marcar la pagina actual como disponible en audio_available.
                  const a = s.audio_available ?? { cover: false, scenes: [] };
                  const nextAvail = onCover
                    ? { ...a, cover: true }
                    : {
                        ...a,
                        scenes: (s.scenes ?? []).map((_, i) =>
                          i === sceneIndex ? true : !!a.scenes?.[i]
                        ),
                      };
                  return { ...s, cost: breakdown, audio_available: nextAvail };
                });
                if (breakdown) logCostBreakdown(story.title, breakdown);
              }}
            />
          )}
          {isReady && (
            <a
              href={`/api/stories/${encodeURIComponent(story.slug)}/pdf`}
              className="inline-flex items-center gap-1 rounded-full bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-brand-700"
            >
              <DownloadIcon size="0.95em" /> PDF
            </a>
          )}
          <ShareButton
            title={story.title}
            url={`/story/${story.slug}`}
            className="rounded-full border border-brand-200 px-3 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-50"
          />
        </div>

        <button
          onClick={goNext}
          disabled={page >= totalPages - 1}
          aria-label="Siguiente"
          className="inline-flex items-center gap-1 rounded-full border border-brand-200 px-3 py-1.5 text-sm font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-40 sm:px-5 sm:py-2"
        >
          <span className="hidden sm:inline">Siguiente</span>
          <ChevronRightIcon size="1em" />
        </button>
      </div>
    </div>
  );
}
